// Astrea Ops Platform - Messages data-access layer
// (direct_messages, project_messages, message_reads)
//
// 30 September 2026. Martin: "messages addressed to someone are not private". They
// weren't - every message was a project message inside projects.app_data, readable by
// anyone who could open the project. See
// supabase/migrations/20260930000001_private_messaging.sql for the full background.
//
// Two kinds of message now, deliberately kept separate so nobody has to guess who can
// read what:
//   Direct messages   private. RLS lets only the sender and the named recipients read
//                     a row - not Senior Management, not System Admin. Optional
//                     project tag for context only; it does not widen access.
//   Project messages  the open team channel on a project, readable by all internal
//                     staff (same as the rest of the project). One row per message
//                     instead of a nested array in app_data, so two people posting on
//                     the same project can no longer overwrite each other.
//
// message_reads holds each person's read markers for anything the Messages inbox
// shows, so unread counts survive a reload and follow the person across devices.

import { supabase } from './supabaseClient';
import { selectAll } from './selectAll';

// PostgREST caps a single select at 1000 rows by default. Page through so a busy
// inbox or a long read history is never silently truncated (shared helper since v7.77).

// ── Direct messages ─────────────────────────────────────────────────────────────

function dmRowToEntry(r) {
  return {
    id: r.id,
    threadId: r.thread_id,
    senderId: r.sender_id || null,
    senderName: r.sender_name || '',
    recipientIds: r.recipient_ids || [],
    projectId: r.project_id || null,
    subject: r.subject || '',
    text: r.body,
    timestamp: r.created_at,
  };
}

// RLS already restricts this to messages the signed-in person sent or received, so
// no filter is needed (or trusted) client-side.
export async function loadDirectMessages() {
  const rows = await selectAll(() =>
    supabase.from('direct_messages').select('*').order('created_at', { ascending: true }).order('id')
  );
  return rows.map(dmRowToEntry);
}

// entry: { id, threadId, senderId, recipientIds, projectId, text }. id must be a real
// UUID (crypto.randomUUID()); for a new conversation threadId === id. sender_name is
// filled in server-side from people.name, so it isn't sent.
// entry.subject (3 Oct 2026) goes on the first message of a conversation. If the
// subject column has not been added yet (migration 20261003000001), the message is
// sent without it rather than failing.
export async function sendDirectMessage(entry) {
  const row = {
    id: entry.id,
    thread_id: entry.threadId || entry.id,
    sender_id: entry.senderId,
    recipient_ids: entry.recipientIds,
    project_id: entry.projectId || null,
    body: entry.text,
  };
  if (entry.subject) row.subject = entry.subject;
  const insert = r => supabase.from('direct_messages').insert(r).select().single();
  let { data, error } = await insert(row);
  if (error && row.subject && String(error.message || '').includes('subject')) {
    const { subject, ...rest } = row;
    ({ data, error } = await insert(rest));
    if (!error) data = { ...data, subject };
  }
  if (error) throw error;
  return dmRowToEntry(data);
}

// ── Project (team) messages ─────────────────────────────────────────────────────

// Same field names the app has always used on project.projectMessages, so the
// Progress and Activity tabs keep working unchanged.
function pmRowToEntry(r) {
  return {
    id: r.id,
    projectId: r.project_id,
    authorId: r.author_id || null,
    authorName: r.author_name || '',
    text: r.body,
    mentions: r.mentions || [],
    timestamp: r.created_at,
    // v7.107: an update posted on a task (it used to be a comment kept on the task)
    taskId: r.task_id || null,
    taskName: r.task_name || '',
    // v7.108: a reply carries the id of the Update it answers
    parentId: r.parent_id || null,
  };
}

// Map of projectId -> [messages, oldest first].
export async function loadProjectMessagesByProject() {
  const rows = await selectAll(() =>
    supabase.from('project_messages').select('*').order('created_at', { ascending: true }).order('id')
  );
  const map = new Map();
  rows.forEach(r => {
    const e = pmRowToEntry(r);
    if (!map.has(e.projectId)) map.set(e.projectId, []);
    map.get(e.projectId).push(e);
  });
  return map;
}

// entry.taskId / entry.taskName (v7.107) link an update to the task it was posted on. Until
// migration 20261023000001 adds those columns, the update is posted without the link rather
// than failing.
export async function postProjectMessage(projectId, entry) {
  const row = {
    id: entry.id,
    project_id: projectId,
    author_id: entry.authorId,
    body: entry.text,
    mentions: entry.mentions || [],
  };
  if (entry.taskId) {
    row.task_id = entry.taskId;
    row.task_name = entry.taskName || '';
  }
  // v7.108: a reply. Never posted without its parent (it would become a new Update), so a
  // missing parent_id column (migration 20261023000002 not run yet) is an error.
  if (entry.parentId) row.parent_id = entry.parentId;
  const insert = r => supabase.from('project_messages').insert(r).select().single();
  let { data, error } = await insert(row);
  if (error && row.task_id && /task_(id|name)/.test(String(error.message || ''))) {
    const { task_id, task_name, ...rest } = row;
    ({ data, error } = await insert(rest));
  }
  if (error) throw error;
  const saved = pmRowToEntry(data);
  if (entry.taskId && !saved.taskId) {
    saved.taskId = entry.taskId;
    saved.taskName = entry.taskName || '';
  }
  if (entry.parentId && !saved.parentId) saved.parentId = entry.parentId;
  return saved;
}

// ── Read markers ────────────────────────────────────────────────────────────────

export async function loadReadIds() {
  const rows = await selectAll(() => supabase.from('message_reads').select('item_id').order('person_id').order('item_id'));
  return rows.map(r => r.item_id);
}

export async function markItemsRead(personId, itemIds) {
  const ids = Array.from(new Set((itemIds || []).filter(Boolean)));
  if (!personId || !ids.length) return;
  const { error } = await supabase
    .from('message_reads')
    .upsert(ids.map(item_id => ({ person_id: personId, item_id })), { onConflict: 'person_id,item_id', ignoreDuplicates: true });
  if (error) throw error;
}
